/**
 * User Tools Modal
 * Shows the tools used by a specific user with dates and session counts
 */

import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, Package, RefreshCw, Search, Calendar, Activity, Clock } from 'lucide-react';
import '../AIGarage.css';

// Only rendered when a tool/user row actually carries a timeSavedMin value
// (Tools Dashboard's Time Saved / ROI tab). Usage tab callers never pass this
// field, so their rendering is unaffected.
const formatMinutesSaved = (minutes) => {
  if (minutes == null || Number.isNaN(Number(minutes))) return '—';
  const hrs = Number(minutes) / 60;
  if (hrs >= 10) return `${Math.round(minutes).toLocaleString()}m (${hrs.toFixed(1)} hrs)`;
  return `${Math.round(minutes).toLocaleString()}m`;
};

const UserToolsModal = ({ isOpen, user, userTools, loading, onClose, getUserInitials, getUserColor }) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Filter tools based on search query
  const filteredTools = useMemo(() => {
    if (!userTools) return [];
    if (!searchQuery.trim()) return userTools;

    const query = searchQuery.toLowerCase();
    return userTools.filter(tool =>
      tool.toolTitle.toLowerCase().includes(query) ||
      tool.toolType.toLowerCase().includes(query)
    );
  }, [userTools, searchQuery]);

  if (!isOpen || !user) return null;

  const modalContent = (
    <div className="garage-modal-overlay" onClick={onClose}>
      <div className="garage-modal-container" style={{ maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="garage-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            {/* User Avatar */}
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: getUserColor ? getUserColor(0) : '#4ECDC4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: '600',
                fontSize: '18px',
                flexShrink: 0
              }}
            >
              {getUserInitials ? getUserInitials(user.userEmail) : 'U'}
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="garage-modal-title" style={{ marginBottom: '4px' }}>
                {user.userName}
              </h2>
              <p style={{ fontSize: '13px', color: 'var(--garage-text-3)', margin: 0 }}>
                {user.userEmail}
              </p>
            </div>
          </div>
          <button className="garage-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="garage-modal-body" style={{ padding: 0 }}>
          {/* Stats Header */}
          <div style={{
            padding: '16px 24px',
            backgroundColor: 'var(--garage-bg-2)',
            borderBottom: '1px solid var(--garage-border)',
            display: 'flex',
            gap: '24px',
            alignItems: 'center'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: 'var(--garage-text-3)', marginBottom: '4px' }}>
                Total Sessions
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--garage-blue)' }}>
                {user.sessions || 0}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: 'var(--garage-text-3)', marginBottom: '4px' }}>
                Tools Used
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--garage-orange)' }}>
                {user.toolsUsed || 0}
              </div>
            </div>
            {user.timeSavedMin != null && (
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: 'var(--garage-text-3)', marginBottom: '4px' }}>
                  Time Saved
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#ea580c' }}>
                  {formatMinutesSaved(user.timeSavedMin)}
                </div>
              </div>
            )}
          </div>

          {/* Search Bar */}
          <div style={{
            padding: '16px 24px',
            backgroundColor: 'var(--garage-surface)',
            borderBottom: '1px solid var(--garage-border)'
          }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--garage-text-3)'
              }} />
              <input
                type="text"
                placeholder="Search tools..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid var(--garage-border)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'var(--garage-bg)',
                  color: 'var(--garage-text)'
                }}
              />
            </div>
          </div>

          {/* Section Header */}
          <div style={{
            padding: '12px 24px',
            backgroundColor: 'var(--garage-bg-2)',
            borderBottom: '1px solid var(--garage-border)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Package size={18} style={{ color: 'var(--garage-green)' }} />
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--garage-text-2)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Tools Used ({filteredTools.length})
            </span>
          </div>

          {/* Tools List */}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '48px', textAlign: 'center', color: 'var(--garage-text-3)' }}>
                <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
                <div style={{ marginTop: '12px', fontSize: '14px' }}>Loading tools...</div>
              </div>
            ) : filteredTools && filteredTools.length > 0 ? (
              <div>
                {filteredTools.map((tool, idx) => (
                  <div
                    key={tool.toolId}
                    style={{
                      padding: '16px 24px',
                      borderBottom: idx < filteredTools.length - 1 ? '1px solid var(--garage-border-2)' : 'none',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--garage-bg-2)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', width: '100%' }}>
                      {/* Tool Info */}
                      <div style={{ flex: 1, minWidth: 0, overflow: 'visible' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                          <div style={{
                            fontSize: '15px',
                            fontWeight: '600',
                            color: 'var(--garage-text)',
                            wordBreak: 'break-word'
                          }}>
                            {tool.toolTitle}
                          </div>
                          <span className={`garage-tag garage-tag-${tool.toolType === 'App' ? 'blue' : 'orange'}`}>
                            {tool.toolType}
                          </span>
                          {tool.environment && (
                            <span style={{
                              fontSize: '10px',
                              fontWeight: '600',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: tool.environment === 'prd' ? '#10b981' : tool.environment === 'test' ? '#f59e0b' : '#6366f1',
                              color: 'white',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {tool.environment}
                            </span>
                          )}
                          {tool.roiStatus === 'unconfigured' && (
                            <span style={{
                              fontSize: '9px',
                              fontWeight: '600',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: 'var(--garage-surface-2)',
                              color: 'var(--garage-text-2)'
                            }}>
                              UNCONFIGURED
                            </span>
                          )}
                        </div>

                        {/* Date Information */}
                        <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--garage-text-3)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Calendar size={12} />
                            <span>Last: {formatDate(tool.lastAccessedAt)}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Calendar size={12} />
                            <span>First: {formatDate(tool.firstAccessedAt)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Time Saved (Time Saved / ROI tab only) + Sessions Count */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', flexShrink: 0 }}>
                        {tool.timeSavedMin != null && (
                          <div style={{ textAlign: 'right' }}>
                            <div style={{
                              fontSize: '20px',
                              fontWeight: '700',
                              color: '#ea580c',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <Clock size={16} />
                              {formatMinutesSaved(tool.timeSavedMin)}
                            </div>
                            <div style={{
                              fontSize: '11px',
                              color: 'var(--garage-text-3)',
                              marginTop: '2px'
                            }}>
                              time saved
                            </div>
                          </div>
                        )}
                        <div style={{ textAlign: 'right' }}>
                          <div style={{
                            fontSize: '20px',
                            fontWeight: '700',
                            color: 'var(--garage-blue)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Activity size={16} />
                            {tool.sessionCount}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: 'var(--garage-text-3)',
                            marginTop: '2px'
                          }}>
                            sessions
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : searchQuery.trim() ? (
              <div style={{ padding: '48px', textAlign: 'center', color: 'var(--garage-text-3)', fontSize: '14px' }}>
                No tools found matching "{searchQuery}"
              </div>
            ) : (
              <div style={{ padding: '48px', textAlign: 'center', color: 'var(--garage-text-3)', fontSize: '14px' }}>
                No tool usage data available for this user
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default UserToolsModal;
